import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout from '../../components/TrackerLayout';
import { useAdminOrCoordinatorSession } from '../../context/TrackerContext';
import { getAllComplaints, updateComplaintStatus, markProjectComplete } from '../../lib/api';
import type { ComplaintItem } from '../../lib/api';

const STATUS_OPTIONS = ['Open', 'In Progress', 'Needs Info', 'Resolved'];
const STATUS_COLORS: Record<string, string> = {
  'Open':        '#FF6535',
  'In Progress': '#F0B429',
  'Needs Info':  '#6366f1',
  'Resolved':    '#22c55e',
};

export default function AdminComplaints() {
  const session = useAdminOrCoordinatorSession();
  const adminPassword = session?.kind === 'admin' ? session.adminPassword : session?.kind === 'coordinator' ? session.adminPassword : '';

  const [complaints, setComplaints] = useState<ComplaintItem[]>([]);
  const [loading, setLoading]       = useState(true);
  const [filter, setFilter]         = useState<string>('');
  const [selected, setSelected]     = useState<ComplaintItem | null>(null);
  const [newStatus, setNewStatus]   = useState('');
  const [note, setNote]             = useState('');
  const [saving, setSaving]         = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllComplaints(adminPassword, filter || undefined);
      setComplaints(res.complaints);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [adminPassword, filter]);

  useEffect(() => { load(); }, [load]);

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected || !newStatus) return;
    setSaving(true);
    try {
      await updateComplaintStatus(adminPassword, selected._id, newStatus, note);
      toast.success('Complaint updated — email sent');
      setSelected(null); setNote(''); setNewStatus('');
      load();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const openCount = complaints.filter(c => c.status !== 'Resolved').length;

  return (
    <TrackerLayout>
      <div className="p-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-black" style={{ color: '#2b2240' }}>Student Complaints</h1>
            <p className="text-sm text-gray-500 mt-0.5">{openCount} open complaint{openCount !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex items-center gap-2">
            <select value={filter} onChange={e => setFilter(e.target.value)}
              className="px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-yellow-400">
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <button onClick={load} className="px-4 py-2 rounded-xl text-sm font-bold text-white" style={{ background: '#2b2240' }}>
              Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-400">Loading…</div>
        ) : complaints.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-4xl mb-3">✉️</p>
            <p className="text-gray-400 text-sm">No complaints {filter ? `with status "${filter}"` : 'yet'}.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* List */}
            <div className="space-y-3">
              {complaints.map(c => (
                <button key={c._id} onClick={() => { setSelected(c); setNewStatus(c.status); setNote(''); }}
                  className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${selected?._id === c._id ? 'border-yellow-400' : 'border-gray-100 bg-white hover:border-gray-200'}`}
                  style={selected?._id === c._id ? { background: '#fffbeb' } : {}}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="text-sm font-bold" style={{ color: '#2b2240' }}>{c.title}</p>
                    <span className="text-xs px-2 py-0.5 rounded-full font-bold flex-shrink-0 text-white"
                      style={{ background: STATUS_COLORS[c.status] || '#9ca3af' }}>
                      {c.status}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 font-semibold">{c.studentName}</p>
                  <p className="text-xs text-gray-400">
                    {new Date(c.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                </button>
              ))}
            </div>

            {/* Detail */}
            {selected ? (
              <div className="bg-white rounded-2xl border-2 border-gray-100 p-5 h-fit sticky top-6">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <h2 className="text-base font-bold" style={{ color: '#2b2240' }}>{selected.title}</h2>
                  <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">×</button>
                </div>

                <div className="text-xs space-y-1 mb-4 pb-4 border-b border-gray-100">
                  <div><span className="text-gray-400 w-20 inline-block">Student:</span><span className="font-semibold text-gray-700">{selected.studentName}</span></div>
                  <div><span className="text-gray-400 w-20 inline-block">Email:</span><span className="text-gray-600">{selected.studentEmail}</span></div>
                  <div><span className="text-gray-400 w-20 inline-block">Status:</span>
                    <span className="font-bold" style={{ color: STATUS_COLORS[selected.status] }}>{selected.status}</span>
                  </div>
                </div>

                <p className="text-sm text-gray-600 mb-4 pb-4 border-b border-gray-100">{selected.description}</p>

                {/* History */}
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">History</h4>
                <div className="space-y-2 mb-4 pb-4 border-b border-gray-100">
                  {(selected.history || []).map((h, i) => (
                    <div key={i} className="flex gap-2">
                      <div className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5"
                        style={{ background: STATUS_COLORS[h.status] || '#9ca3af' }} />
                      <div>
                        <p className="text-xs font-bold" style={{ color: STATUS_COLORS[h.status] || '#6b7280' }}>{h.status}</p>
                        {h.note && <p className="text-xs text-gray-600">{h.note}</p>}
                        <p className="text-xs text-gray-400">
                          {new Date(h.updatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Update form */}
                {selected.status !== 'Resolved' && (
                  <form onSubmit={handleUpdate} className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Update Status</label>
                      <select value={newStatus} onChange={e => setNewStatus(e.target.value)}
                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-yellow-400">
                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Message to Student</label>
                      <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} placeholder="Add a note (sent via email)…"
                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-yellow-400 resize-none" />
                    </div>
                    <button type="submit" disabled={saving}
                      className="w-full py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-50"
                      style={{ background: '#2b2240' }}>
                      {saving ? 'Updating…' : 'Update & Send Email'}
                    </button>
                  </form>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-2xl border-2 border-gray-100 p-8 text-center">
                <p className="text-3xl mb-2">👈</p>
                <p className="text-sm text-gray-400">Select a complaint to view details and respond.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </TrackerLayout>
  );
}
